import * as React from 'react';
import { throttle } from '@cian/newbuilding-utils';
import classNames from 'clsx';

import {
  SCROLL_THROTTLE_DELAY,
  SCROLL_CLICK_DEAD_ZONE,
  RESIZE_THROTTLE_DELAY,
  SCROLL_ANIMATION_DELAY,
} from '../../constants/verticalSlider';
import { VerticalArrow } from '../VerticalArrow';

import {
  getFullLeftScrollOffset,
  getFullRightScrollOffset,
  getLeftScrollOffset,
  getRightScrollOffset,
  getSizeClassName,
} from './helpers';
import { scrollToPos, IScrollToPos } from './helpers/scrollTo';
import * as styles from './ItemsCarousel.css';
import { IArrowButtonProps, TScrollSize } from './types';

export interface IItemsCarouselProps {
  arrowButton?: React.ComponentType<IArrowButtonProps>;
  arrowOffset?: number;
  onItemClick?(index: number): void;
  scrollVisible?: boolean;
  size?: TScrollSize;
}

export type TItemsCarouselProps = React.PropsWithChildren<IItemsCarouselProps>;

interface IItemsCarouselState {
  offsetWidth: number;
  scrollLeft: number;
  scrollWidth: number;
}

export class ItemsCarousel extends React.Component<TItemsCarouselProps, IItemsCarouselState> {
  private scrollRef = React.createRef<HTMLUListElement>();
  private startX: number;
  private scrollLeft: number;
  private scrollToPos: IScrollToPos | null = null;
  private isDragging = false;
  private isScrolling = false;

  public state = {
    offsetWidth: 0,
    scrollLeft: 0,
    scrollWidth: 0,
  };

  public componentDidMount() {
    this.resetScrollState();
    window.addEventListener('resize', this.handleResize);
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  private get leftArrowVisible() {
    const { scrollLeft } = this.state;

    return scrollLeft > 0;
  }

  private get rightArrowVisible() {
    const { scrollLeft, offsetWidth, scrollWidth } = this.state;

    return scrollLeft < scrollWidth - offsetWidth;
  }

  public render() {
    const { size, arrowOffset, arrowButton: ArrowButton = VerticalArrow } = this.props;

    return (
      <div className={styles['wrapper']}>
        <ul
          className={classNames(styles['container'], getSizeClassName(size))}
          ref={this.scrollRef}
          onMouseDown={this.handleMouseDown}
          onScroll={this.throttledHandleScroll}
        >
          {React.Children.map(
            this.props.children,
            (child, i) =>
              child && (
                <li key={i} className={classNames(styles['item'])} onClick={this.handleItemClick(i)}>
                  {child}
                </li>
              ),
          )}
        </ul>
        {this.leftArrowVisible && <ArrowButton direction={-1} offset={arrowOffset} onClick={this.onLeftArrowClick} />}
        {this.rightArrowVisible && <ArrowButton direction={1} offset={arrowOffset} onClick={this.onRightArrowClick} />}
      </div>
    );
  }

  private resetScrollState = () => {
    const container = this.scrollRef.current;

    /* istanbul ignore else */
    if (container) {
      const { offsetWidth, scrollLeft, scrollWidth } = container;
      this.setState({ offsetWidth, scrollLeft: Math.ceil(scrollLeft), scrollWidth });
    }
  };

  private handleResize = throttle(RESIZE_THROTTLE_DELAY, this.resetScrollState);

  private handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    this.stopScrolling();

    const container = this.scrollRef.current;

    /* istanbul ignore else */
    if (container) {
      this.startX = e.pageX;
      this.scrollLeft = container.scrollLeft;

      window.addEventListener('mousemove', this.handleMove);
      window.addEventListener('mouseup', this.handleMoveEnd);
    }

    this.isDragging = false;
  };

  private handleMoveEnd = () => {
    window.removeEventListener('mousemove', this.handleMove);
    window.removeEventListener('mousemove', this.handleMoveEnd);
  };

  private handleItemClick = (i: number) => (e: React.MouseEvent) => {
    if (this.isDragging) {
      e.stopPropagation();
      e.preventDefault();

      return;
    }

    const { onItemClick } = this.props;

    /* istanbul ignore else */
    if (onItemClick) {
      onItemClick(i);
    }
  };

  private handleMove = (e: MouseEvent) => {
    const container = this.scrollRef.current;

    /* istanbul ignore else */
    if (container) {
      const walk = e.pageX - this.startX;

      if (Math.abs(walk) >= SCROLL_CLICK_DEAD_ZONE) {
        this.isDragging = true;
      }

      let scrollLeft = this.scrollLeft - walk;
      const maxScrollLeft = container.scrollWidth - container.offsetWidth;

      if (scrollLeft < 0) {
        scrollLeft = 0;
      }

      if (scrollLeft > maxScrollLeft) {
        scrollLeft = maxScrollLeft;
      }

      container.scrollLeft = scrollLeft;
    }
  };

  private handleScroll = () => {
    const container = this.scrollRef.current;

    /* istanbul ignore else */
    if (container) {
      if (!this.isScrolling) {
        this.setState({ scrollLeft: Math.ceil(container.scrollLeft) });
      }
    }
  };

  private throttledHandleScroll = throttle(SCROLL_THROTTLE_DELAY, this.handleScroll);

  private onRightArrowClick = () => {
    const container = this.scrollRef.current;
    const { scrollLeft } = this.state;
    const { scrollVisible } = this.props;

    /* istanbul ignore else */
    if (container) {
      const scrollOffset = scrollVisible
        ? getFullRightScrollOffset(container, scrollLeft)
        : getRightScrollOffset(container, scrollLeft);

      this.stopScrolling();
      this.scrollTo(scrollOffset);
    }
  };

  private onLeftArrowClick = () => {
    const container = this.scrollRef.current;
    const { scrollLeft } = this.state;
    const { scrollVisible } = this.props;

    /* istanbul ignore else */
    if (container) {
      const scrollOffset = scrollVisible
        ? getFullLeftScrollOffset(container, scrollLeft)
        : getLeftScrollOffset(container, scrollLeft);

      this.stopScrolling();
      this.scrollTo(scrollOffset);
    }
  };

  private scrollTo = (scrollOffset: number) => {
    const container = this.scrollRef.current;

    /* istanbul ignore else */
    if (container) {
      this.isScrolling = true;
      this.setState({ scrollLeft: scrollOffset });

      container.addEventListener('scrollEnd', this.stopScrolling);
      this.scrollToPos = scrollToPos(container, scrollOffset, SCROLL_ANIMATION_DELAY);
    }
  };

  private stopScrolling = () => {
    if (this.scrollToPos) {
      this.scrollToPos.cancel();
      this.scrollToPos = null;
    }

    this.isScrolling = false;

    const container = this.scrollRef.current;

    /* istanbul ignore else */
    if (container) {
      container.removeEventListener('scrollEnd', this.stopScrolling);
    }
  };
}
